/**
 * Web 端专属：能力不可用时的「下载桌面版」引导弹窗
 *
 * Web 端点击任何需要桌面能力的按钮时，由 bridge/web.ts 派发 niuren:need-desktop 事件，
 * 这里统一弹窗引导下载。
 */
<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { resolveDesktopDownload } from '@/utils/desktopDownload'
import { toast } from '@/composables/useToast'

const visible = ref(false)
const tip = ref('')

/** 当前系统的安装包情况：Mac 版还在开发中，按钮文案要如实反映 */
const dl = resolveDesktopDownload()
const btnText = computed(() => {
  if (dl.os === 'mac') return 'Mac 版开发中'
  if (dl.os === 'windows') return '下载 Windows 版'
  return '下载桌面版'
})

function onNeed(e: Event) {
  tip.value = (e as CustomEvent).detail?.tip || ''
  visible.value = true
}

onMounted(() => window.addEventListener('niuren:need-desktop', onNeed))
onUnmounted(() => window.removeEventListener('niuren:need-desktop', onNeed))

function download() {
  if (dl.available && dl.url) {
    window.open(dl.url, '_blank')
    visible.value = false
    return
  }
  // Mac / 其它系统：没有包就只提示，弹窗留着让用户「稍后再说」
  toast.info(dl.tip || '暂未提供当前系统的安装包', 3000)
}
</script>

<template>
  <el-dialog v-model="visible" title="需要桌面版客户端" width="460px" append-to-body destroy-on-close>
    <div class="body">
      <div class="hero">
        <el-icon :size="40" color="var(--brand)"><Monitor /></el-icon>
      </div>
      <p class="desc">{{ tip || '多平台一键发布需要调用本地浏览器登录态，网页版无法完成，请使用桌面版客户端。' }}</p>

      <ul class="feat-list">
        <li><el-icon><Select /></el-icon>一次登录，长期保持平台登录态</li>
        <li><el-icon><Select /></el-icon>内容一键同步发布到多个自媒体平台</li>
        <li><el-icon><Select /></el-icon>发布结果实时回传，失败可重试</li>
      </ul>
    </div>

    <template #footer>
      <el-button @click="visible = false">稍后再说</el-button>
      <el-button type="primary" @click="download">
        <el-icon><Download /></el-icon>{{ btnText }}
      </el-button>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.body {
  text-align: center;
}

.hero {
  color: var(--brand);
  margin-bottom: 12px;
}

.desc {
  color: var(--text-sub);
  font-size: var(--fs-base);
  line-height: 1.7;
  margin: 0 0 16px;
}

.feat-list {
  list-style: none;
  margin: 0;
  padding: 16px;
  background: var(--bg-hover);
  border-radius: var(--radius-md);
  text-align: left;

  li {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--fs-base);
    color: var(--text-main);
    padding: 5px 0;
  }
}
</style>
